/* eslint-disable no-nested-ternary */
import debounce from 'lodash-es/debounce';
import isNil from 'lodash-es/isNil';
import omitBy from 'lodash-es/omitBy';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraColumn } from '../../../components/AtiraColumn';
import { AtiraEmpty } from '../../../components/AtiraEmpty';
import { AtiraRow } from '../../../components/AtiraRow';
import { AtiraSelect } from '../../../components/AtiraSelect';
import { Flex } from '../../../components/Flex';
import { Input } from '../../../components/Input';
import { SpinnerFullScreen } from '../../../components/SpinnerFullScreen';
import { Text } from '../../../components/Text';
import { AtiraLinkThemes } from '../../../model/atira-link/types/AtiraLinkThemes.enum';
import { AtiraLinkTheme } from '../../../model/theme/AtiraLinkTheme';
import { GetThemesDto } from '../../../model/theme/dto/GetThemesDto';
import { AtiraLinkPlanThemeTier } from '../../../model/theme/type/AtiraLinkPlanThemeTier';
import { adminSliceSelectors } from '../../../redux/admin/admin.selector';
import { adminActions } from '../../../redux/admin/admin.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { Breakpoints } from '../../../theme/Breakpoints';
import { Spacing } from '../../../theme/Spacing';
import { TemplatePreviewModal } from '../../shared/components/TemplatePreviewModal';
import { AdminThemeCard } from '../components/AdminThemeCard';
import { AdminThemeManageModal } from '../components/AdminThemeManageModal';

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  height: 100%;
`;

const StyledSpinner = styled(SpinnerFullScreen)`
  margin: auto;
`;

const StyledColumn = styled(AtiraColumn)`
  padding-left: 0 !important;
  padding-right: 0 !important;

  @media (min-width: ${Breakpoints.MOBILE}) {
    &:nth-child(odd) {
      padding-inline-end: ${Spacing.m} !important;
    }

    &:nth-child(even) {
      padding-inline-start: ${Spacing.m} !important;
    }
  }

  @media (min-width: ${Breakpoints.TABLET}) {
    &:nth-child(odd),
    &:nth-child(even) {
      padding-inline-start: 0 !important;
      padding-inline-end: 0 !important;
    }

    &:nth-child(3n - 1) {
      padding-inline: ${Spacing.m} !important;
    }
  }

  @media (min-width: ${Breakpoints.DESKTOP}) {
    &:nth-child(3n - 1) {
      padding-inline: 0 !important;
    }

    &:not(:nth-child(4n)) {
      padding-inline-end: ${Spacing.m} !important;
    }
  }

  @media (min-width: ${Breakpoints.LARGE_DESKTOP}) {
    &:not(:nth-child(4n)) {
      padding-inline-end: 0 !important;
    }

    &:not(:nth-child(6n)) {
      padding-inline-end: ${Spacing.m} !important;
    }
  }
`;

const ThemesCountText = styled(Text)`
  font-weight: bold;
  margin-inline-start: auto;
  width: 100%;
  text-align: end;
`;

const Header = styled(Flex)`
  gap: ${Spacing.s};
  flex-direction: column;

  @media (min-width: ${Breakpoints.TABLET}) {
    flex-direction: row;
    align-items: center;
  }
`;

const SearchInputWrapper = styled(Flex)`
  @media (min-width: ${Breakpoints.TABLET}) {
    max-width: 12rem !important;
  }
`;

export const AdminThemesTab: React.FC = () => {
  const [manageModalVisible, setManageModalVisible] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<AtiraLinkTheme | null>(
    null,
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const { t } = useTranslation();
  const { control, getValues } = useForm<GetThemesDto>();
  const dispatch = useAppDispatch();

  const themes = useAppSelector(adminSliceSelectors.selectAllThemes);
  const themesLoading = useAppSelector(adminSliceSelectors.selectThemesLoading);

  const onManageTheme = (theme: AtiraLinkTheme) => {
    setSelectedTheme(theme);
    setManageModalVisible(true);
  };

  const onSelectTheme = (theme: AtiraLinkTheme) => {
    setSelectedTheme(theme);
    setIsPreviewOpen(true);
  };

  const fetchMoreThemes = useCallback(
    () =>
      dispatch(
        adminActions.getThemes(
          omitBy(
            {
              meta: {
                page: 0,
                count: 1000,
              },
              ...getValues(),
            },
            isNil,
          ),
        ),
      ),
    [dispatch, getValues],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(debounce(fetchMoreThemes, 1000), [
    fetchMoreThemes,
  ]);

  useEffect(() => {
    fetchMoreThemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container>
      <Header>
        <Controller
          control={control}
          name="keyword"
          render={({ field: { value, onChange } }) => (
            <SearchInputWrapper>
              <Input
                value={value}
                onChange={(e) => {
                  onChange(e.target.value);
                  debouncedSearch();
                }}
                placeholder={t('common.search')}
              />
            </SearchInputWrapper>
          )}
        />

        <Flex alignItems="center" gap="s">
          <Controller
            control={control}
            name="tier"
            render={({ field: { value, onChange } }) => (
              <AtiraSelect
                style={{ width: '8rem' }}
                options={Object.values(AtiraLinkPlanThemeTier).map((tier) => ({
                  label: t(`theme.tier.${tier.toLowerCase()}`),
                  value: tier,
                }))}
                defaultValue={AtiraLinkPlanThemeTier.PLAN_0}
                value={value}
                onChange={(e) => {
                  onChange(e);
                  fetchMoreThemes();
                }}
              />
            )}
          />

          <ThemesCountText>
            {t('admin.themes.count', { count: themes.length })}
          </ThemesCountText>
        </Flex>
      </Header>

      {themesLoading && !themes?.length ? (
        <StyledSpinner />
      ) : !themes?.length ? (
        <AtiraEmpty description={t('common.template.empty')} />
      ) : (
        <AtiraRow gutter={[20, 20]}>
          {themes.map((theme) => (
            <StyledColumn key={theme._id} xs={24} sm={12} md={8} lg={6} xl={4}>
              <AdminThemeCard
                theme={theme}
                onManage={onManageTheme}
                onSelectTheme={onSelectTheme}
              />
            </StyledColumn>
          ))}
        </AtiraRow>
      )}

      <TemplatePreviewModal
        selectedTheme={selectedTheme?.name as AtiraLinkThemes}
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
      />

      <AdminThemeManageModal
        open={manageModalVisible}
        onClose={() => setManageModalVisible(false)}
        theme={selectedTheme}
      />
    </Container>
  );
};
